import { centsToFormattedDollars } from '@fruitful/fruitils';
import React from 'react';
import {
    BodyMediumEmphasized,
    BodySmallRegular,
    Icon,
    LabelRegular,
    MonoLabelRegular,
    TriangleAlert,
    View,
    XStack,
    YStack,
} from 'smoothie';

import {
    lineageVerdict,
    type SimulatorLineageFinding,
    type SimVerdict,
} from '../../hooks/useSimulateIncomeSplit';

const VERDICT_META: Record<
    SimVerdict,
    { label: string; color: string; background: string }
> = {
    pass: { label: 'Pass', color: '$green900', background: '$green300' },
    warn: { label: 'Warning', color: '$orange1100', background: '$orange300' },
    fail: { label: 'Fail', color: '$red1000', background: '$red200' },
};

/**
 * Compact pill surfaced in list rows. Only renders when the audit trips
 * (warn/fail) to avoid noise on clean/absent results.
 */
export const LineageAuditBadge = ({
    finding,
}: {
    finding?: SimulatorLineageFinding | null;
}) => {
    const verdict = lineageVerdict(finding);
    if (verdict === 'pass') {
        return null;
    }

    const meta = VERDICT_META[verdict];

    return (
        <XStack
            backgroundColor={meta.background}
            borderRadius={16}
            paddingHorizontal={8}
            paddingVertical={4}
            alignSelf="flex-start"
            alignItems="center"
            gap={4}
        >
            <Icon icon={TriangleAlert} size={12} color={meta.color} />
            <MonoLabelRegular color={meta.color}>
                Lineage: {meta.label}
            </MonoLabelRegular>
        </XStack>
    );
};

const LineageDetailRow = ({
    label,
    value,
}: {
    label: string;
    value: string;
}) => (
    <XStack gap={8} justifyContent="space-between" flexWrap="wrap">
        <LabelRegular color="$gray1000">{label}</LabelRegular>
        <MonoLabelRegular color="$gray1200">{value}</MonoLabelRegular>
    </XStack>
);

/**
 * Full lineage-audit status panel for a single simulation result. Always shows
 * the verdict; expands with the implicated rules and dollar amounts when it
 * trips. Pass `finding` straight off the simulate response's `lineageAudit`.
 */
export const LineageAuditPanel = ({
    finding,
}: {
    finding?: SimulatorLineageFinding | null;
}) => {
    const verdict = lineageVerdict(finding);
    const meta = VERDICT_META[verdict];
    const tripped = verdict !== 'pass';

    const statusText =
        finding == null
            ? 'Lineage audit did not run (unknown — treated as pass)'
            : verdict === 'fail'
              ? 'Member has already over-contributed beyond the cap'
              : verdict === 'warn'
                ? 'Routing rules lost original lineage; contribution caps are undercounting (latent)'
                : 'Lineage is intact; contribution caps are tracking correctly';

    return (
        <YStack
            padding={12}
            borderRadius={12}
            gap={8}
            backgroundColor={tripped ? meta.background : '$gray100'}
            borderWidth={1}
            borderColor={tripped ? meta.color : '$gray500'}
        >
            <XStack gap={8} alignItems="center">
                {tripped && (
                    <Icon icon={TriangleAlert} size={16} color={meta.color} />
                )}
                <BodyMediumEmphasized
                    color={tripped ? meta.color : '$gray1200'}
                >
                    Lineage audit: {meta.label}
                </BodyMediumEmphasized>
            </XStack>
            <BodySmallRegular color={tripped ? meta.color : '$gray1000'}>
                {statusText}
            </BodySmallRegular>

            {finding != null && tripped && (
                <View
                    padding={8}
                    borderRadius={8}
                    backgroundColor="$white"
                    gap={6}
                >
                    <LineageDetailRow
                        label="Affected rule IDs"
                        value={
                            finding.affectedRuleIds.length > 0
                                ? finding.affectedRuleIds.join(', ')
                                : 'None'
                        }
                    />
                    <LineageDetailRow
                        label="Orphaned YTD contributions"
                        value={centsToFormattedDollars(
                            finding.totalOrphanedYearlyCents,
                            false,
                        )}
                    />
                    {verdict === 'fail' && (
                        <>
                            <LineageDetailRow
                                label="Over-contributed beyond cap"
                                value={centsToFormattedDollars(
                                    finding.totalRealizedOverContributionCents,
                                    false,
                                )}
                            />
                            <LineageDetailRow
                                label="Over-contribution months"
                                value={
                                    finding.overContributionMonths.length > 0
                                        ? finding.overContributionMonths.join(
                                              ', ',
                                          )
                                        : 'None'
                                }
                            />
                        </>
                    )}
                </View>
            )}
        </YStack>
    );
};
